import { Tab } from "@headlessui/react";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { InputSize, Select, Show } from "shared/components";
import { cx } from "shared/utils";

type Tab = string;

interface ITabSelector<T extends Tab> {
  tabs: T[];
  setActiveTab: (value: T) => void;
  selected: T;
  displayMap?: Record<T, ReactNode>;
  variant?: "filled" | "underline" | "folder" | "pill";
  tabClassName?: string;
  tabProps?: Record<T, TabProps>;
}

interface TabProps {
  disabled?: boolean;
}

const SCROLL_THRESHOLD = 3;

const useHorizontalScroll = () => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (viewport) {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      setScrollState({
        left: scrollLeft > SCROLL_THRESHOLD,
        right: scrollLeft + clientWidth < scrollWidth - SCROLL_THRESHOLD
      });
    }
  }, []);

  const adjustScroll = useCallback(
    (delta: number) => {
      const viewport = viewportRef.current;
      if (viewport) {
        viewport.scrollLeft += delta;
        updateScrollState();
      }
    },
    [updateScrollState]
  );

  //Scroll the tab bar when holding down the blurred area on the left or right
  const handleScroll = useCallback(
    (direction: "left" | "right") => {
      let isScrolling = true;
      const scrollStep = direction === "left" ? -10 : 10;
      const scroll = () => {
        if (isScrolling) {
          adjustScroll(scrollStep);
          requestAnimationFrame(scroll);
        }
      };
      document.addEventListener("mouseup", () => (isScrolling = false), {
        once: true
      });
      scroll();
    },
    [adjustScroll]
  );

  //horizontal scroll without shift key
  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (e.deltaX === 0 && e.deltaY !== 0) {
        e.preventDefault();
        adjustScroll(e.deltaY);
      }
    },
    [adjustScroll]
  );

  //Blur the left or right tab list when scrolling is possible in that direction
  useEffect(() => {
    const viewport = viewportRef.current;
    viewport?.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();
    return () => viewport?.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  return { viewportRef, scrollState, onWheel, handleScroll };
};

const variantStyles: Record<
  "filled" | "underline" | "folder" | "pill",
  [
    string,
    {
      base: string;
      selected: string;
      inactive: string;
    }
  ]
> = {
  filled: [
    "flex space-x-1 rounded-lg bg-[#1C1D220A] p-1",
    {
      base: "w-full rounded-md border px-4 py-2 text-xs transition focus:outline-none",
      selected:
        "border-alpha-100 bg-white font-bold text-content-1000 shadow-sm",
      inactive:
        "border-transparent font-medium text-content-300 shadow-transparent"
    }
  ],
  pill: [
    "flex",
    {
      base: "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition focus:outline-none",
      selected: "bg-primary-20 text-primary-500",
      inactive: "text-content-300"
    }
  ],
  folder: [
    "flex",
    {
      base: "rounded-md rounded-b-none border border-b-0 px-4 py-3 text-xs transition transition focus:outline-none",
      selected:
        "border-l-alpha-100 border-r-alpha-100 border-t-alpha-100 font-bold text-content-1000 bg-white folder-tab-active",
      inactive: "font-medium text-content-300 shadow-transparent bg-alpha-40"
    }
  ],
  underline: [
    "border-b border-alpha-40 flex space-x-4",
    {
      base: "px-0 py-2 outline-none text-sm font-medium transition",
      selected:
        "border-b-2 border-primary-500 pb-[6px] text-primary-500 !font-bold",
      inactive: "text-content-400"
    }
  ]
};

/** @deprecated Use `Tabs` from `shared/components/Tabs` instead. */
export const Tabs = <T extends Tab>({
  setActiveTab,
  tabs,
  selected,
  displayMap,
  variant = "filled",
  tabClassName,
  tabProps
}: ITabSelector<T>) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [tabListClx, tabClx] = useMemo(() => variantStyles[variant], [variant]);

  const { viewportRef, scrollState, onWheel, handleScroll } =
    useHorizontalScroll();

  const handleTabClick = useCallback(
    (tab: T, index: number) => {
      setActiveTab(tab);
      const tabElement = tabRefs.current[index];
      if (tabElement)
        tabElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
    },
    [setActiveTab]
  );

  return (
    <>
      <Show if={variant === "filled"}>
        <Select
          className={cx("md:hidden w-full")}
          id={"tab-selector" + variant}
          value={selected}
          legacyErrorMargins={false}
          size={InputSize.SM}
          onChange={(e) => setActiveTab(e.target.value as T)}
          options={tabs.map((tab) => ({
            value: String(tab),
            label: (
              <p className="flex gap-2">
                {displayMap && displayMap[tab] ? displayMap[tab] : tab}
              </p>
            )
          }))}
        />
      </Show>
      <div
        className={cx(
          "relative",
          // Horizontal scroll isn't visible this way and doesn't break things
          variant === "folder" && "overflow-x-auto hide-scroll"
        )}
      >
        {(["left", "right"] as const).map((direction) =>
          scrollState[direction] ? (
            <div
              key={direction}
              className={cx(
                "absolute inset-y-0 w-8 cursor-pointer",
                direction === "left"
                  ? "left-0 bg-gradient-to-r from-white via-white to-transparent"
                  : "right-0 bg-gradient-to-l from-white via-white to-transparent"
              )}
              onMouseDown={() => handleScroll(direction)}
            />
          ) : null
        )}
        <Tab.Group>
          <Tab.List
            ref={viewportRef}
            onWheel={onWheel}
            className={cx(
              "max-w-full hide-scroll",
              // Underline isn't visible this way
              variant === "folder"
                ? "border-b border-alpha-100"
                : "overflow-x-auto",
              tabListClx,
              variant === "filled" ? "hidden md:flex" : "flex"
            )}
          >
            {tabs.map((tab, index) => {
              return (
                <Tab
                  ref={(el) =>
                    (tabRefs.current[index] = el as HTMLButtonElement | null)
                  }
                  onClick={() => handleTabClick(tab, index)}
                  key={tab}
                  className={cx(
                    tabClx.base,
                    selected === tab ? tabClx.selected : tabClx.inactive,
                    tabClassName
                  )}
                  disabled={tabProps?.[tab].disabled}
                >
                  <div className="flex items-center justify-center gap-2 whitespace-nowrap">
                    {displayMap?.[tab] || tab}
                  </div>
                </Tab>
              );
            })}
          </Tab.List>
        </Tab.Group>
      </div>
    </>
  );
};
